import type { ReactNode } from 'react'

import { t } from '../i18n/lingua'

/** Il badge dell'esito di un'esecuzione di backup (pagina Backup e pagina del job). */
export function EsitoBadge({ result }: { result: string | null }) {
  const r = (result ?? '').toLowerCase()
  if (!r || r === 'none') return <span className="muted">—</span>
  if (r === 'success') return <span className="badge badge--ok">{t('Successo')}</span>
  if (r === 'warning') return <span className="badge badge--warn">{t('Avviso')}</span>
  if (r === 'failed') return <span className="badge badge--error">{t('Fallito')}</span>
  if (r === 'canceled') return <span className="badge badge--info">{t('Annullato')}</span>
  return <span className="badge badge--info">{result}</span>
}

/** L'etichetta del fornitore: i nomi propri non si traducono. */
export const FORNITORI: Record<string, string> = {
  veeam: 'Veeam',
  vinchin: 'Vinchin',
  commvault: 'Commvault',
  rubrik: 'Rubrik',
}
export function fornitore(v?: string): string {
  return v ? FORNITORI[v] ?? v : '—'
}

/** I dettagli tecnici di una risposta (forme dei dati non riconosciute,
 *  letture secondarie non riuscite): chiusi, servono a validare un fornitore
 *  nuovo senza dover leggere il log del server. */
export function Diagnostica({ voci }: { voci?: string[] }) {
  if (!voci?.length) return null
  return (
    <details className="muted">
      <summary>{t('Dettagli tecnici')}</summary>
      <ul>
        {voci.map((v, i) => (
          <li key={i} className="mono">
            {v}
          </li>
        ))}
      </ul>
    </details>
  )
}

/** Una durata in secondi, leggibile. */
export function formatSecondi(s: number | null | undefined): string {
  if (s == null) return '—'
  if (s < 60) return `${s} s`
  const h = Math.floor(s / 3600)
  const min = Math.floor((s % 3600) / 60)
  if (h > 0) return `${h} h ${min} min`
  return `${min} min ${s % 60} s`
}

/** La velocità di un'esecuzione in GB/h: dati elaborati sulla durata. Solo
 *  quando il prodotto dà entrambi come numeri. */
export function velocita(r: { processed_bytes: number | null; duration_s: number | null }): number | null {
  if (r.processed_bytes == null || !r.duration_s) return null
  return r.processed_bytes / 1024 ** 3 / (r.duration_s / 3600)
}

export function formatVelocita(v: number | null): string {
  if (v == null) return '—'
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} GB/h`
}

/** Una barra orizzontale per voce, ordinate dal valore più alto: per
 *  confrontare job fra loro (velocità, durata). Una tinta sola; il valore
 *  scritto accanto, formattato da `formato`; l'etichetta porta al dettaglio. */
export function BarreValore({
  voci,
  formato,
}: {
  voci: { key: string; label: ReactNode; value: number; title?: string }[]
  formato: (v: number) => string
}) {
  if (!voci.length) return <p className="muted">{t('Nessun dato da confrontare.')}</p>
  const max = Math.max(...voci.map((v) => v.value), 1)
  const ordinate = [...voci].sort((a, b) => b.value - a.value)
  return (
    <div className="bars">
      {ordinate.map((v) => (
        <div className="bars__row" key={v.key} title={v.title}>
          <span className="bars__label">{v.label}</span>
          <span className="bars__track">
            <span className="bars__fill" style={{ width: `${Math.max((v.value / max) * 100, 0.8)}%` }} />
          </span>
          <span className="bars__value">{formato(v.value)}</span>
        </div>
      ))}
    </div>
  )
}
